import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import {
  Statistic,
  Card,
  Row,
  Col,
  Tag,
  Table,
  Empty,
  Space,
  Divider,
  Descriptions
} from 'antd'
import { ShopOutlined } from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import { getRequest, dutyCustomerDetailUrl } from '../../../config'
import { formatMoney } from '../../Analytics/utils'
import { CONV_STATE_COLORS, carStatusTag, conversationPath } from '../statusColors'
import IdentityLabel from '../../../components/IdentityLabel'

// The full picture for one customer: activity metrics + their conversations, duty requests and
// payments. Used both by the routed detail page (new tab) and the list's quick-view modal, so the
// two stay identical. Fetches by `id`; re-fetches when `id` changes.
const CustomerDetailBody = ({ id }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  useEffect(() => {
    const load = async () => {
      const res = await getRequest(dutyCustomerDetailUrl(id), 'customer', setLoading)
      if (res.success) setData(res.data)
    }
    if (id) load()
  }, [id])

  if (!data) {
    return (
      <PageLoader loading={loading}>
        {!loading ? <Empty description="Customer not found" /> : <div style={{ height: 120 }} />}
      </PageLoader>
    )
  }

  const dealer = data.dealer
  const convColumns = [
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: s => <Tag color={CONV_STATE_COLORS[s] || 'default'}>{s}</Tag>
    },
    {
      title: 'Cars',
      key: 'cars',
      render: (_, r) => (
        <span>
          {r.carCount || 0}
          {(r.carStatuses || []).length ? (
            <div style={{ marginTop: 2 }}>
              {r.carStatuses.map((s, i) => carStatusTag(s, i))}
            </div>
          ) : null}
        </span>
      )
    },
    {
      title: 'Cars detail',
      key: 'summaries',
      render: (_, r) => {
        const s = (r.carSummaries || []).filter(x => x && x.trim())
        return s.length ? s.map((x, i) => <div key={i}>{x}</div>) : <span style={{ color: '#888' }}>—</span>
      }
    },
    {
      title: 'Last activity',
      dataIndex: 'lastInboundAt',
      key: 'lastInboundAt',
      render: v => (v ? new Date(v).toLocaleString() : '—')
    },
    {
      title: '',
      key: 'action',
      width: 90,
      render: (_, r) => (
        <Link
          to={conversationPath(r.service, r.id)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open chat
        </Link>
      )
    }
  ]

  const requestColumns = [
    {
      title: 'Car',
      dataIndex: 'summary',
      key: 'summary',
      render: v => v || <span style={{ color: '#888' }}>(no details yet)</span>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: s => carStatusTag(s)
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, r) =>
        r.priceAmount ? formatMoney(r.priceAmount, r.priceCurrency) : '—'
    },
    {
      title: 'Paid at',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: v => (v ? new Date(v).toLocaleString() : '—')
    }
  ]

  const txnColumns = [
    { title: 'Reference', dataIndex: 'reference', key: 'reference' },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, r) => formatMoney(r.amount, r.currency)
    },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <Tag>{s}</Tag> },
    { title: 'Method', dataIndex: 'method', key: 'method' },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: v => (v ? new Date(v).toLocaleString() : '—')
    }
  ]

  return (
    <div>
      {/* Header */}
      <Space align="center" wrap>
        <h3 style={{ margin: 0 }}>
          <IdentityLabel value={data} strong />
        </h3>
        {data.isRegisteredDealer ? (
          <Tag icon={<ShopOutlined />} color="green">
            Registered dealer
          </Tag>
        ) : null}
      </Space>
      <div style={{ color: '#888', marginTop: 4 }}>
        {data.momoName ? `${data.momoName} · ` : ''}
        {data.momoNetwork || ''}
        {data.momoPhone ? ` · MoMo ${data.momoPhone}` : ''}
      </div>
      {dealer ? (
        <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
          <Descriptions.Item label="Dealer">
            {dealer.companyName}
            {dealer.discountPercent ? ` · ${dealer.discountPercent}% discount` : ''}
          </Descriptions.Item>
        </Descriptions>
      ) : null}

      {/* Metric cards */}
      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Duty requests" value={data.requestCount || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Completed" value={data.completedCount || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Paid" value={data.paidCount || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Revenue"
              value={formatMoney(data.revenue, 'GHS')}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ color: '#888', marginTop: 8, fontSize: 12 }}>
        {data.conversationCount || 0} conversation(s) · first seen{' '}
        {data.createdAt ? new Date(data.createdAt).toLocaleDateString() : '—'} · last active{' '}
        {data.lastActivity ? new Date(data.lastActivity).toLocaleString() : '—'}
      </div>

      <Divider orientation="left" style={{ marginTop: 20 }}>
        Conversations
      </Divider>
      <Table
        size="small"
        rowKey="id"
        dataSource={data.conversations || []}
        columns={convColumns}
        pagination={false}
        scroll={{ x: 700 }}
        locale={{ emptyText: <Empty description="No conversations" /> }}
      />

      <Divider orientation="left" style={{ marginTop: 20 }}>
        Duty requests
      </Divider>
      <Table
        size="small"
        rowKey="id"
        dataSource={data.dutyRequests || []}
        columns={requestColumns}
        pagination={false}
        scroll={{ x: 600 }}
        locale={{ emptyText: <Empty description="No duty requests" /> }}
      />

      <Divider orientation="left" style={{ marginTop: 20 }}>
        Payments
      </Divider>
      <Table
        size="small"
        rowKey="id"
        dataSource={data.transactions || []}
        columns={txnColumns}
        pagination={false}
        scroll={{ x: 600 }}
        locale={{ emptyText: <Empty description="No payments" /> }}
      />
    </div>
  )
}

CustomerDetailBody.propTypes = {
  id: PropTypes.string
}

export default CustomerDetailBody
